/**
 * Shared by the three quick quote steps (18 Oct 2026): the pinned button bar
 * on phones, and the one scroll to the first invalid field after a failed
 * Next or Submit (PR #136, moved here from quick-quote-part-1.tsx).
 */

/**
 * The button bar. On phones it stays pinned to the bottom of the screen while
 * the form is in view, with the iPhone home-bar inset below the button. It is
 * the last thing in the form, so at the end of the page it sits in its own
 * place and covers nothing. From the sm breakpoint up it is a plain block.
 */
export const QQ_STICKY_BAR_CLS =
  "sticky bottom-0 z-10 -mx-4 border-t border-border bg-background px-4 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2 sm:static sm:mx-0 sm:border-0 sm:p-0";

/** Marks the sticky "Step N of 3" bar (QuickQuoteStepLabel). */
export const QQ_PROGRESS_ATTR = "data-qq-progress";

/**
 * Bottom of the sticky band at the top of the screen: the site header, plus
 * the step progress bar when it is sticky and the row is in its section (the
 * bar sticks only inside its own column). The progress bar is measured where
 * it will be once stuck, its CSS top plus its height, because the scroll
 * that follows always leaves it stuck: the row is below it in the page.
 */
function stickyBandBottom(row: HTMLElement, offsetTop: number): number {
  let bottom = 0;
  const header =
    document.querySelector<HTMLElement>("header[data-site-header]") ??
    document.querySelector<HTMLElement>("header");
  if (header && getComputedStyle(header).position === "sticky") {
    bottom = Math.max(0, header.getBoundingClientRect().bottom - offsetTop);
  }
  const progress = document.querySelector<HTMLElement>(`[${QQ_PROGRESS_ATTR}]`);
  if (progress && progress.parentElement?.contains(row)) {
    const cs = getComputedStyle(progress);
    const top = parseFloat(cs.top);
    if (cs.position === "sticky" && Number.isFinite(top)) {
      bottom = Math.max(bottom, top + progress.getBoundingClientRect().height - offsetTop);
    }
  }
  return bottom;
}

/**
 * Scroll so the row of the first invalid field (label, box and message)
 * sits at the top of what the visitor can see, just under the sticky header
 * and the step progress bar. A banner (step 1's "fill in your details"
 * message) goes directly above it when both fit (18 Oct 2026).
 *
 * Everything is measured against window.visualViewport where there is
 * one. On iOS the keyboard shrinks the visual viewport but not
 * window.innerHeight, and Safari can offset the visual viewport inside the
 * layout viewport; getBoundingClientRect is relative to the layout
 * viewport, so offsetTop is taken off.
 *
 * Phones (the button bar is sticky): always align to the top, so the
 * fields below stay clear of the bar. Desktop: scroll only when the block
 * is not already fully in view.
 */
export function scrollFieldToTop(
  row: HTMLElement,
  banner: HTMLElement | null,
  bar: HTMLElement | null,
  smooth: boolean,
): void {
  const vv = window.visualViewport;
  const offsetTop = vv?.offsetTop ?? 0;
  const viewHeight = vv?.height ?? window.innerHeight;
  const bandTop = stickyBandBottom(row, offsetTop) + 8;
  const rowRect = row.getBoundingClientRect();
  const rowBottom = rowRect.bottom - offsetTop;
  let blockTop = rowRect.top - offsetTop;
  if (banner) {
    const bannerTop = banner.getBoundingClientRect().top - offsetTop;
    if (rowBottom - bannerTop <= viewHeight - bandTop - 8) blockTop = bannerTop;
  }
  const barSticky = !!bar && getComputedStyle(bar).position === "sticky";
  if (!barSticky && blockTop >= bandTop && rowBottom <= viewHeight - 12) return;
  const delta = blockTop - bandTop;
  if (Math.abs(delta) < 2) return;
  window.scrollBy({ top: delta, behavior: smooth ? "smooth" : "auto" });
}

/**
 * Bring the first invalid field into view after a failed Next or Submit.
 * Call it from a layout effect, after the red borders and messages render.
 *
 * Touch screens (18 Oct 2026, two iPhone tests): focusing the field opens
 * the keyboard, and Safari then scrolls the page itself. Any scroll of ours
 * after that fights it and the page flickers. So on a touch screen, scroll
 * once, instantly, and do not focus: no keyboard opens, and the visitor taps
 * the field to start typing, which Safari handles as a normal focus. A field
 * still focused from earlier typing is blurred so its keyboard closes first.
 * A screen reader hears the problem from the page's polite live region.
 *
 * Mouse and keyboard: scroll, then focus so a screen reader reads the
 * message through aria-describedby.
 */
export function scrollToInvalid(
  row: HTMLElement,
  control: HTMLElement | null,
  banner: HTMLElement | null,
  bar: HTMLElement | null,
): void {
  const touch = window.matchMedia?.("(pointer: coarse)").matches ?? false;
  if (touch) {
    const active = document.activeElement;
    if (
      active instanceof HTMLInputElement ||
      active instanceof HTMLSelectElement ||
      active instanceof HTMLTextAreaElement
    ) {
      active.blur();
    }
    scrollFieldToTop(row, banner, bar, false);
    return;
  }
  const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  scrollFieldToTop(row, banner, bar, !reduce);
  control?.focus({ preventScroll: true });
}
